<template>
    <div class="account-faxes">
        <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="success"
        ></v-progress-linear>
        <div v-if="!loading && !faxes.length" class="account-comm-empty">
            No faxes
        </div>
        <div
            v-for="(fax, index) in faxes"
            :key="fax.id || index"
            class="account-comm-item"
            :class="{ 'account-comm-item--open': fax.expanded }"
        >
            <div class="account-comm-item-header" @click="toggle(fax)">
                <div class="account-comm-item-avatar account-comm-item-avatar--fax">
                    <v-icon small dark>mdi-fax</v-icon>
                </div>
                <div class="account-comm-item-summary">
                    <div class="account-comm-item-title-row">
                        <span class="account-comm-item-title">
                            {{ fax.from || "Unknown sender" }}
                        </span>
                        <v-icon
                            v-if="fax.hasAttachment"
                            small
                            color="#607d8b"
                            class="account-comm-attach-icon"
                        >
                            mdi-paperclip
                        </v-icon>
                    </div>
                    <div class="account-comm-item-preview">
                        {{ fax.subject || fax.fileName || "Fax document" }}
                    </div>
                </div>
                <div class="account-comm-item-meta">
                    <div class="account-comm-item-date">{{ fax.date | time }}</div>
                    <v-chip
                        v-if="fax.communicationStatus === 'PENDING'"
                        small
                        text-color="white"
                        color="#fdb400"
                        class="account-comm-chip"
                    >
                        Pending
                    </v-chip>
                    <v-chip
                        v-else-if="fax.communicationStatus === 'COMPLETED'"
                        small
                        text-color="white"
                        color="success"
                        class="account-comm-chip"
                    >
                        Done
                    </v-chip>
                </div>
                <v-icon class="account-comm-item-chevron">
                    {{ fax.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
            </div>

            <div v-if="fax.expanded" class="account-comm-item-body">
                <div class="account-comm-detail-grid">
                    <div class="account-comm-detail">
                        <span class="account-comm-detail-label">From</span>
                        <span class="account-comm-detail-value">{{ fax.from }}</span>
                    </div>
                    <div class="account-comm-detail" v-if="fax.subject">
                        <span class="account-comm-detail-label">Subject</span>
                        <span class="account-comm-detail-value">{{ fax.subject }}</span>
                    </div>
                </div>

                <fax-preview
                    v-if="fax.id"
                    :key="fax.id"
                    :row="fax"
                    :hideAssign="true"
                ></fax-preview>

                <div class="account-comm-actions" v-if="fax.communicationID && fax.communicationStatus === 'PENDING'">
                    <v-btn
                        color="success"
                        outline
                        small
                        :loading="processingId === fax.communicationID"
                        @click="markComplete(fax)"
                    >
                        <v-icon small class="mr-1">mdi-check</v-icon>
                        Mark done
                    </v-btn>
                </div>
            </div>
        </div>
        <div v-if="hasMore" class="account-comm-load-more">
            <v-btn
                small
                outline
                color="success"
                :loading="loadingMore"
                @click="loadMore"
            >
                Load more faxes
            </v-btn>
        </div>
    </div>
</template>
<script>
const PAGE_SIZE = 25;

export default {
    data() {
        return {
            loading: true,
            loadingMore: false,
            hasMore: false,
            processingId: null,
            faxes: [],
            totalCount: 0,
            user: "",
        };
    },
    async created() {
        this.user = this.$store.state.authentication.user;
        await this.loadCount();
        await this.loadFaxes();
    },
    methods: {
        async loadCount() {
            try {
                this.totalCount = await this.$http.get(
                    "api/faxes/getCountForAccount?accountID=" +
                        this.$route.params.id
                );
            } catch (e) {
                this.totalCount = 0;
            }

            this.$emit("loaded", this.totalCount);
        },
        async loadFaxes() {
            this.loading = true;

            try {
                const faxes = await this.$http.get(
                    "api/faxes/getForAccount?accountID=" +
                        this.$route.params.id +
                        "&skip=0&top=" +
                        PAGE_SIZE
                );
                this.faxes = faxes || [];
                this.hasMore = (faxes || []).length === PAGE_SIZE;
            } catch (e) {
                this.faxes = [];
                this.hasMore = false;
            }

            this.loading = false;
        },
        async loadMore() {
            this.loadingMore = true;

            try {
                const faxes = await this.$http.get(
                    "api/faxes/getForAccount?accountID=" +
                        this.$route.params.id +
                        "&skip=" +
                        this.faxes.length +
                        "&top=" +
                        PAGE_SIZE
                );
                const newFaxes = faxes || [];
                this.faxes = this.faxes.concat(newFaxes);
                this.hasMore = newFaxes.length === PAGE_SIZE;
            } catch (e) {
                this.hasMore = false;
            }

            this.loadingMore = false;
        },
        toggle(fax) {
            this.$set(fax, "expanded", !fax.expanded);
        },
        async markComplete(fax) {
            this.processingId = fax.communicationID;

            try {
                await this.$http.post("api/communications/updateStatus", {
                    communicationID: fax.communicationID,
                    status: "COMPLETED",
                    user: this.user,
                });
                this.$notify({
                    group: "notification",
                    text: "Fax marked done",
                    type: "success",
                });
                await this.loadFaxes();
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not update fax",
                    type: "error",
                });
            }

            this.processingId = null;
        },
    },
};
</script>
<style>
.account-faxes {
    max-height: 600px;
    overflow-y: auto;
    padding: 6px;
}

.account-comm-item-avatar--fax {
    background: #8d6e63;
}

.account-faxes .fax-preview {
    max-width: none;
    padding: 0;
}

.account-faxes .fax-file-name {
    font-size: 12px;
}

.account-faxes .fax-plain-text {
    font-size: 11px;
    color: #90a4ae;
    line-height: 1.35;
    margin-bottom: 8px !important;
}

.account-faxes .fax-preview-frame {
    height: 60vh;
}
</style>
